import { useEffect, useRef, useState } from "react";
import { Bot, X, Send } from "lucide-react";
import { API } from "@/lib/api";

function renderMd(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
}

const GREETING = "Hi! Not sure what device you've got? Describe it — colour, screen size, charging port, any text on the back — and I'll help you identify it.";

function getSessionId() {
  let id = localStorage.getItem("appleman_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("appleman_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const line = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.token) {
              const t = obj.token;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + t };
                return copy;
              });
            } else if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: obj.error };
                return copy;
              });
            }
          } catch { /* partial json, skip */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the workshop brain. Try again in a moment — or message us on WhatsApp." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3" data-testid="chat-widget">
      {open && (
        <div className="w-[92vw] max-w-sm border-2 border-black bg-white shadow-hard-lg flex flex-col" data-testid="chat-panel">
          <div className="bg-black text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-accent" />
              <div>
                <p className="font-display font-bold text-sm uppercase tracking-wider leading-none">Help me identify my device</p>
                <p className="text-[11px] text-neutral-400 mt-1">AI assistant — Appleman Tech Repair</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close-btn"><X className="h-5 w-5" /></button>
          </div>
          <div ref={scrollRef} className="h-80 overflow-y-auto p-4 space-y-3 bg-neutral-50" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed border-2 border-black whitespace-pre-wrap ${
                m.role === "user" ? "ml-auto bg-accent shadow-hard-sm" : "bg-white shadow-hard-sm"
              }`} data-testid={`chat-message-${m.role}`}>
                {m.content ? renderMd(m.content) : (busy && i === messages.length - 1 ? "…" : "")}
              </div>
            ))}
          </div>
          <div className="border-t-2 border-black p-3 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Describe your device…"
              data-testid="chat-input"
              className="flex-1 border-2 border-black px-3 py-2 text-sm outline-none focus:shadow-hard-sm"
            />
            <button onClick={send} disabled={busy} data-testid="chat-send-btn" aria-label="Send"
              className="border-2 border-black bg-black text-white px-3 shadow-hard-sm brutal-hover disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-btn"
        className="flex items-center gap-2 border-2 border-black bg-accent px-4 py-3 font-display text-sm font-bold uppercase tracking-wider shadow-hard brutal-hover"
      >
        <Bot className="h-5 w-5" />
        {open ? "Close" : "Identify my device"}
      </button>
    </div>
  );
}
